import React, { useState, useRef } from 'react'
import { ListStyles } from '../styles'
import { Document, useDocumentShareLink } from '../../../../services/documents.service'
import { formatFileSize } from '../../../../utils/utils'
import { format, parseISO, isValid } from 'date-fns'
import { Field } from '../../../../components/Input/CheckboxInput/styles'
import { FaCircleInfo, FaFileLines, FaFolder, FaSpinner, FaShieldHalved } from 'react-icons/fa6'
import { FaSearch } from 'react-icons/fa'
import { ButtonRound } from '../../../../components/Header/styles'
import { Item } from './styles'
import ModalViewer, { ModalHandle } from '../../../../components/Modals/ModalDocumentViewer'; // Importando o modal
import OverlayLoading from '../../../../components/OverlayLoading'; // Importando o overlay de loading
import { useUserStore } from '../../../../store/user'
import { usePermissions } from '../../../../hooks/usePermissions'

export interface ListItemHandle {
  checkValue: (value: boolean) => void
}

export interface ListItemProps {
  file: Document
  onClickItem: (file: Document, e: any) => void
  onDoubleClickItem: (file: Document, e: any) => void
  isEven?: boolean
}

const ListItem: React.ForwardRefRenderFunction<ListItemHandle, ListItemProps> = (
  { file, onClickItem, onDoubleClickItem, isEven = true },
  ref,
) => {
  const [checked, setChecked] = React.useState(false)
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null)
  const modalRef = useRef<ModalHandle>(null)
  const overlayRef = useRef<any>(null)
  const { user } = useUserStore()
  const { canEditDocuments } = usePermissions()

  // Verificar permissões baseadas em grupo ao invés de role do usuário
  const canEdit = canEditDocuments


  /**
   * Obtém a data preferencial para exibição
   * Prioriza: data dos metadata > data de criação
   */
  const getDisplayDate = (document: Document): Date | null => {
    try {
      // Verificar se existe documentTypeWithData e metadata
      if (document.documentTypeWithData?.metadata && Array.isArray(document.documentTypeWithData.metadata)) {
        // Procurar por campos de data nos metadata
        const dateFields = ['data', 'date', 'data_criacao', 'data_criado', 'created_at', 'data_documento'];
        
        for (const meta of document.documentTypeWithData.metadata) {
          if (meta.name && meta.value && dateFields.some(field => 
            meta.name.toLowerCase().includes(field.toLowerCase())
          )) {
            try {
              // Tentar diferentes formatos de data
              const dateFormats = [
                'dd/MM/yyyy',
                'dd/MM/yyyy HH:mm',
                'yyyy-MM-dd',
                'yyyy-MM-dd HH:mm:ss',
                'dd-MM-yyyy',
                'MM/dd/yyyy'
              ];
              
              for (const dateFormat of dateFormats) {
                try {
                  const parsedDate = parseISO(meta.value);
                  if (isValid(parsedDate)) {
                    return parsedDate;
                  }
                } catch {
                  // Tentar parse com format específico
                  try {
                    const parsedDate = parseISO(meta.value.replace(/(\d{2})\/(\d{2})\/(\d{4})/, '$3-$2-$1'));
                    if (isValid(parsedDate)) {
                      return parsedDate;
                    }
                  } catch {
                    continue;
                  }
                }
              }
            } catch (error) {
              console.warn('Erro ao processar data dos metadata:', error);
              continue;
            }
          }
        }
      }
      
      // Fallback para data de criação - verificar se é válida
      if (document.createdAt) {
        try {
          const createdAt = new Date(document.createdAt);
          if (isValid(createdAt)) {
            return createdAt;
          }
        } catch (error) {
          console.warn('Erro ao processar createdAt:', error);
        }
      }
      
      // Se tudo falhar, retornar null
      return null;
    } catch (error) {
      console.warn('Erro geral em getDisplayDate:', error);
      return null;
    }
  }

  // Hook para obter link de compartilhamento quando um documento é selecionado
  const { data: shareLinkData, isLoading: isLoadingShareLink, error: shareLinkError } = useDocumentShareLink(selectedDocumentId)

  React.useImperativeHandle(
    ref,
    () => ({
      checkValue(value) {
        setChecked(value)
      },
      checked,
      _id: file._id,
      url: file.url,
      name: file.name,
    }),
    [checked, file],
  )

  // Função para abrir o modal com o documento
  const handleOpenDocument = () => {
    if (!file.isFolder && file._id) {
      // Mostrar overlay de loading
      overlayRef.current?.handleOpen()
      setSelectedDocumentId(file._id)
    }
  }

  // Função para abrir pasta
  const handleOpenFolder = () => {
    if (file.isFolder) {
      onDoubleClickItem(file, { stopPropagation: () => {} })
    }
  }

  // Efeito para abrir o modal quando o link de compartilhamento for obtido
  React.useEffect(() => {
    if (shareLinkData?.success && shareLinkData.shareLink) {
      // Fechar overlay de loading
      overlayRef.current?.handleClose()
      modalRef.current?.openModal(shareLinkData.shareLink, shareLinkData.document)
      setSelectedDocumentId(null) // Reset para evitar re-abertura
    }
  }, [shareLinkData])

  // Efeito para tratar erros
  React.useEffect(() => {
    if (shareLinkError) {
      console.error('Erro ao obter link de compartilhamento:', shareLinkError)
      // Fechar overlay de loading em caso de erro
      overlayRef.current?.handleClose()
      setSelectedDocumentId(null)
    }
  }, [shareLinkError])

  return (
    <>
      <ListStyles.Row
        key={file._id}
        onClick={file.isFolder ? handleOpenFolder : handleOpenDocument}
        onDoubleClick={file.isFolder ? (e) => onDoubleClickItem(file, e) : undefined}
        style={{ 
          cursor: 'pointer',
          opacity: isLoadingShareLink ? 0.7 : 1,
          backgroundColor: checked ? 'rgba(44, 48, 175, 0.15)' : 'transparent',
          borderLeft: checked ? '3px solid #2c30af' : '3px solid transparent',
        }}
      >
        {/* Coluna 1: Nome do Arquivo / Pasta (38%) */}
        <ListStyles.NameFile style={{ width: '38%', padding: '0 8px' }}>
          <Field
            onClick={(e) => {
              e.stopPropagation()
            }}
            onChange={(e) => setChecked(e.target.checked)}
            checked={checked}
            type='checkbox'
            style={{ cursor: 'pointer', marginRight: 4 }}
          />

          {canEdit && (
            <button
              type="button"
              data-tooltip-id="my-tooltip"
              data-tooltip-content="Detalhes e Metadados"
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                borderRadius: '4px',
                color: '#94a3b8',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#0284c7'
                e.currentTarget.style.backgroundColor = '#f1f5f9'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#94a3b8'
                e.currentTarget.style.backgroundColor = 'transparent'
              }}
              onClick={(e) => {
                e.stopPropagation()
                onClickItem(file, e)
              }}
            >
              <FaCircleInfo size={15} />
            </button>
          )}

          <div style={{ display: 'flex', alignItems: 'center', margin: '0 4px' }}>
            {isLoadingShareLink && selectedDocumentId === file._id ? (
              <FaSpinner size={18} style={{ animation: 'spin 1s linear infinite', color: '#64748b' }} />
            ) : file.isFolder ? (
              <FaFolder size={18} color="#f59e0b" />
            ) : (
              <FaFileLines size={18} color="#64748b" />
            )}
          </div>

          <ListStyles.Name style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center', flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', gap: '6px', width: '100%' }}>
              <span style={{ fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {file.name}
              </span>
              {(file.isSigned || (file.signatures && file.signatures.length > 0)) && (
                <span
                  title={`Documento Assinado Digitalmente em ${file.signedAt ? new Date(file.signedAt).toLocaleString() : ''}`}
                  style={{
                    backgroundColor: '#e6f4ea',
                    color: '#137333',
                    fontSize: '10px',
                    fontWeight: 700,
                    padding: '2px 6px',
                    borderRadius: '10px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '3px',
                    border: '1px solid #ceead6',
                    flexShrink: 0
                  }}
                >
                  <FaShieldHalved size={10} color="#137333" />
                  ICP-Brasil
                </span>
              )}
              {file.ocrStatus === 'completed' && (
                <span title="OCR Aplicado - Conteúdo pesquisável" style={{ color: '#4caf50', fontSize: '0.8em', flexShrink: 0 }}>
                  <FaSearch size={11} />
                </span>
              )}
            </div>
            {file.ocrSnippet && (
              <span style={{ fontSize: '0.75em', color: '#64748b', marginTop: 2, fontStyle: 'italic', maxWidth: '350px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {file.ocrSnippet}
              </span>
            )}
          </ListStyles.Name>
        </ListStyles.NameFile>

        {/* Coluna 2: Data (16%) */}
        <ListStyles.NameFile style={{ justifyContent: 'flex-start', width: '16%', padding: '0 8px' }}>
          <span style={{ fontSize: '13px', color: '#64748b' }}>
            {(() => {
              try {
                let displayDate = null;
                if (file.documentTypeWithData?.metadata && Array.isArray(file.documentTypeWithData.metadata)) {
                  const dateFields = ['data', 'date', 'data_criacao', 'data_criado', 'created_at', 'data_documento'];
                  for (const meta of file.documentTypeWithData.metadata) {
                    if (meta.name && meta.value && dateFields.some(field => 
                      meta.name.toLowerCase().includes(field.toLowerCase())
                    )) {
                      try {
                        const parsedDate = new Date(meta.value);
                        if (isValid(parsedDate)) {
                          displayDate = parsedDate;
                          break;
                        }
                      } catch (e) {
                        continue;
                      }
                    }
                  }
                }
                if (!displayDate && file.createdAt) {
                  try {
                    const createdAt = new Date(file.createdAt);
                    if (isValid(createdAt)) {
                      displayDate = createdAt;
                    }
                  } catch (e) {}
                }
                if (displayDate && isValid(displayDate)) {
                  return format(displayDate, 'dd/MM/yyyy');
                }
                return '-';
              } catch (error) {
                return '-';
              }
            })()}
          </span>
        </ListStyles.NameFile>

        {/* Coluna 3: Tipo (22%) */}
        <ListStyles.NameFile style={{ justifyContent: 'flex-start', width: '22%', padding: '0 8px' }}>
          <span style={{ fontSize: '13px', color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {file.isFolder ? '-' : (() => {
              const result = file.documentTypeWithData?.documentType?.name || 
                            (typeof file.documentType === 'object' && file.documentType?.name ? file.documentType.name : null) ||
                            (typeof file.documentType === 'string' ? file.documentType : '-');
              return result;
            })()}
          </span>
        </ListStyles.NameFile>

        {/* Coluna 4: Páginas (12%) */}
        <ListStyles.NameFile style={{ justifyContent: 'flex-start', width: '12%', padding: '0 8px' }}>
          <span style={{ fontSize: '13px', color: '#64748b' }}>
            {file.isFolder ? '-' : (file.pages ? `${file.pages} ${file.pages === 1 ? 'pág' : 'págs'}` : '-')}
          </span>
        </ListStyles.NameFile>

        {/* Coluna 5: Tamanho (12%) */}
        <ListStyles.NameFile style={{ justifyContent: 'flex-end', width: '12%', padding: '0 8px' }}>
          <span style={{ fontSize: '13px', color: '#64748b' }}>
            {file.isFolder ? '-' : formatFileSize(Number(file?.size || 0))}
          </span>
        </ListStyles.NameFile>
      </ListStyles.Row>
      
      {/* Modal para visualização do documento */}
      <ModalViewer ref={modalRef} />
      
      {/* Overlay de loading */}
      <OverlayLoading ref={overlayRef}>
        Carregando documento...
      </OverlayLoading>
    </>
  )
}

export default React.forwardRef(ListItem)
